import React from 'react';
import { Modal, Form, Select, DatePicker, Button, InputNumber } from 'antd';
import { AccountType } from '../Types/AccountType';
import { SubscriptionLevel } from '../Types/SubscriptionLevel';
import { mapSubscriptionLevel } from '../Mappers/mapSubscriptionLevel';
import dayjs from 'dayjs';

interface Props {
    visible: boolean;
    onCancel: () => void;
    onSubmit: (values: { subscriptionLevel: SubscriptionLevel; date: string; smsCredits: number; accountType: AccountType }) => void;
    initialLevel: SubscriptionLevel;
    initialDate: string;
    initialSmsCredits: number;
    initialAccountType: AccountType;
}

const subscriptionOptions = Object.values(SubscriptionLevel)
    .filter((v) => typeof v === 'number')
    .map((level) => ({
        value: level as SubscriptionLevel,
        label: mapSubscriptionLevel(level as SubscriptionLevel),
    }));

const accountTypeOptions = [
    { value: AccountType.Standard, label: 'Standard' },
    { value: AccountType.Trial, label: 'Trial' },
];

const AdminSubscriptionForm: React.FC<Props> = ({ visible, onCancel, onSubmit, initialLevel, initialDate, initialSmsCredits, initialAccountType }) => {
    const [form] = Form.useForm();

    React.useEffect(() => {
        form.setFieldsValue({
            subscriptionLevel: initialLevel,
            date: initialDate ? dayjs(initialDate) : undefined,
            smsCredits: initialSmsCredits,
            accountType: initialAccountType,
        });
    }, [initialLevel, initialDate, initialSmsCredits, initialAccountType, form]);

    const handleFinish = (values: any) => {
        onSubmit({
            subscriptionLevel: values.subscriptionLevel,
            date: values.date ? values.date.format('YYYY-MM-DD') : '',
            smsCredits: values.smsCredits ?? 0,
            accountType: values.accountType ?? AccountType.Standard,
        });
    };

    return (
        <Modal
            open={visible}
            title="Update Subscription"
            onCancel={onCancel}
            footer={null}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleFinish}
                initialValues={{ subscriptionLevel: initialLevel, date: initialDate ? dayjs(initialDate) : undefined, smsCredits: initialSmsCredits, accountType: initialAccountType }}
            >
                <Form.Item
                    name="subscriptionLevel"
                    label="Subscription Level"
                    rules={[{ required: true, message: 'Please select a subscription level' }]}
                >
                    <Select options={subscriptionOptions} />
                </Form.Item>
                <Form.Item
                    name="date"
                    label="Next Subscription Date"
                    rules={[{ required: true, message: 'Please select a date' }]}
                >
                    <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                </Form.Item>
                <Form.Item
                    name="accountType"
                    label="Account type"
                    extra="Trial: the date above is when the trial ends. Becomes Standard automatically when the customer pays."
                >
                    <Select data-testid="admin-subscription-account-type" options={accountTypeOptions} />
                </Form.Item>
                <Form.Item
                    name="smsCredits"
                    label="SMS Credits"
                    rules={[{ required: true, message: 'Please enter SMS credits' }]}
                >
                    <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item>
                    <Button type="primary" htmlType="submit" style={{ marginRight: 8 }}>
                        Save
                    </Button>
                    <Button onClick={onCancel}>Cancel</Button>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default AdminSubscriptionForm;
